import { useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";

export default function WelcomePendingModal({ onClose }) {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleClose = async () => {
    setLoading(true);
    try {
      // Marcar en Firestore que ya se mostró — no volverá a aparecer
      // en ningún dispositivo del usuario
      await updateDoc(doc(db, "users", currentUser.uid), {
        welcomeShown: true,
      });
    } catch (err) {
      console.error("Error guardando welcomeShown:", err);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[92vh]">

        {/* ── Header ── */}
        <div className="bg-gradient-to-r from-yellow-400 to-amber-500 px-6 py-5 flex flex-col items-center gap-1 shrink-0">
          <span className="text-5xl">👋</span>
          <h2 className="text-2xl font-extrabold text-black text-center mt-1">
            ¡Bienvenido a SIT!
          </h2>
          <p className="text-sm font-semibold text-black/60 tracking-wide">
            Social Interactive Technology
          </p>
        </div>

        {/* ── Body (scrollable) ── */}
        <div className="overflow-y-auto px-6 py-5 space-y-5 text-sm text-gray-700 dark:text-gray-300">

          {/* Estado de verificación */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-xl px-4 py-3 flex gap-3 items-start">
            <span className="text-2xl shrink-0">⏳</span>
            <div>
              <p className="font-semibold text-yellow-800 dark:text-yellow-200 text-base">
                Tu cuenta está siendo verificada
              </p>
              <p className="text-yellow-700 dark:text-yellow-300 mt-1">
                Nuestro equipo está revisando tu registro. Este proceso toma entre{" "}
                <strong>1 y 6 horas</strong>. Recibirás una notificación en cuanto sea aprobada y
                tendrás acceso completo a la plataforma.
              </p>
            </div>
          </div>

          {/* Qué puedes hacer ahora */}
          <div>
            <p className="font-semibold text-gray-800 dark:text-white mb-3">
              Mientras tanto, puedes explorar:
            </p>
            <ul className="space-y-2.5">
              {[
                {
                  icon: "📰",
                  title: "Feed general",
                  desc: "Lee las publicaciones de toda la comunidad SIT.",
                },
                {
                  icon: "🎓",
                  title: "Mi Carrera",
                  desc: "Explora el feed específico de tu área académica.",
                },
                {
                  icon: "🛒",
                  title: "Market-SIT",
                  desc: "Navega los productos y servicios del marketplace universitario.",
                },
                {
                  icon: "🌐",
                  title: "MultiHub",
                  desc: "Accede a la biblioteca, directorio universitario, acerca de SIT y más.",
                },
              ].map(({ icon, title, desc }) => (
                <li key={title} className="flex items-start gap-2.5">
                  <span className="text-xl shrink-0">{icon}</span>
                  <p>
                    <span className="font-semibold text-gray-800 dark:text-white">
                      {title}
                    </span>{" "}
                    — {desc}
                  </p>
                </li>
              ))}
            </ul>
          </div>

          {/* Antony IA */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl px-4 py-3 flex gap-3 items-start">
            <span className="text-2xl shrink-0">🤖</span>
            <div>
              <p className="font-semibold text-blue-800 dark:text-blue-200">
                ¿Tienes dudas? Antony está aquí para ayudarte
              </p>
              <p className="text-blue-700 dark:text-blue-300 mt-1">
                <strong>Antony</strong> es la inteligencia artificial interna de SIT. Puede
                orientarte sobre cualquier función de la plataforma. ¡Todo está diseñado para
                ser fácil de entender!
              </p>
            </div>
          </div>

          {/* Soporte */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl px-4 py-3 text-center text-xs text-gray-500 dark:text-gray-400">
            ¿Algún problema o consulta?{" "}
            <span className="text-gray-700 dark:text-gray-200 font-medium">
              Contáctanos directamente desde el módulo de{" "}
              <strong>Ayuda</strong> o escríbenos al chat de soporte.
            </span>{" "}
            También puedes escribirnos a{" "}
            <a
              href="mailto:soporte@sit-app.com"
              className="text-blue-500 hover:underline font-medium"
            >
              soporte@sit-app.com
            </a>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 shrink-0">
          <button
            onClick={handleClose}
            disabled={loading}
            className="w-full bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-black font-bold py-3 rounded-xl transition shadow-md disabled:opacity-60 disabled:cursor-not-allowed text-base"
          >
            {loading ? "Guardando..." : "¡Entendido, quiero explorar! 🚀"}
          </button>
        </div>

      </div>
    </div>
  );
}
